import React, { useState, useContext, useMemo, useEffect, useRef, useCallback } from 'react';
import { useNavigate, Navigate, useParams, Link } from 'react-router-dom';
import { TileLayer, WMSTileLayer, FeatureGroup, useMap } from 'react-leaflet';
import { EditControl } from 'react-leaflet-draw';
import leafletImage from 'leaflet-image';
import ChasseHuntMap, { L } from '../components/ChasseHuntMap';
import Header from '../components/Header';
import PrivateLayout from '../components/PrivateLayout';
import api from '../services/api';
import { LangContext } from '../App';
import { useAuth } from '../contexts/AuthContext';
import 'leaflet-draw/dist/leaflet.draw.css';

// ── Design system ─────────────────────────────────────────────────────────────

const VERT = 'var(--couleur-bordure-forte)';
const OR = 'var(--couleur-or)';
const FOND = 'var(--couleur-fond)';
const TEXTE = 'var(--couleur-texte)';
const TEXTE_DIM = 'rgba(255,255,255,0.55)';
const BORDURE = 'rgba(255,255,255,0.14)';

// ── Données référentielles ────────────────────────────────────────────────────

const MILIEUX = [
  { val: 'foret_mixte', fr: 'Forêt mixte', en: 'Mixed forest' },
  { val: 'foret_resineuse', fr: 'Forêt résineuse', en: 'Coniferous forest' },
  { val: 'champs', fr: 'Champs agricoles', en: 'Agricultural fields' },
  { val: 'marecage', fr: 'Marécage', en: 'Wetland' },
  { val: 'mixte', fr: 'Mixte', en: 'Mixed' },
];

const ESPECES = [
  { val: 'orignal', fr: 'Orignal', en: 'Moose' },
  { val: 'chevreuil', fr: 'Chevreuil', en: 'White-tailed deer' },
  { val: 'ours', fr: 'Ours noir', en: 'Black bear' },
  { val: 'caribou', fr: 'Caribou', en: 'Caribou' },
  { val: 'canards', fr: 'Canards', en: 'Ducks' },
  { val: 'outardes', fr: 'Outardes', en: 'Canada geese' },
  { val: 'perdrix', fr: 'Perdrix', en: 'Ruffed grouse' },
  { val: 'lievre', fr: 'Lièvre', en: 'Snowshoe hare' },
  { val: 'coyote', fr: 'Coyote', en: 'Coyote' },
  { val: 'dindon', fr: 'Dindon sauvage', en: 'Wild turkey' },
];

const SERVICES = [
  { val: 'stationnement', fr: 'Stationnement', en: 'Parking' },
  { val: 'chalet', fr: 'Chalet', en: 'Cabin' },
  { val: 'mirador', fr: 'Mirador', en: 'Tree stand' },
  { val: 'sentiers_balises', fr: 'Sentiers balisés', en: 'Marked trails' },
  { val: 'quad_autorise', fr: 'Quad autorisé', en: 'ATV allowed' },
];

// ── Types de zones cartographiques ───────────────────────────────────────────

const ZONES = {
  chasse_autorisee: {
    fr: 'Zone de chasse autorisée',
    en: 'Authorized hunting zone',
    couleur: '#28a84a',
    fillOpacity: 0.3,
    icon: '🟢',
    geom: 'polygon',
  },
  zone_interdite: {
    fr: 'Zone interdite / 150 m habitations',
    en: 'Forbidden zone / 150 m from buildings',
    couleur: '#e53e3e',
    fillOpacity: 0.3,
    icon: '🔴',
    geom: 'polygon',
  },
  acces_stationnement: {
    fr: "Point d'accès / stationnement",
    en: 'Access point / parking',
    couleur: '#D69E2E',
    // Polygons rendered with this type get a translucent fill so they don't
    // obscure underlying terrain — same look as the other zone types.
    // Markers (the original intent here) don't use fillOpacity anyway.
    fillOpacity: 0.3,
    icon: '🟡',
    geom: 'marker',
  },
  sentier: {
    fr: "Sentier d'accès",
    en: 'Access trail',
    couleur: '#3182CE',
    fillOpacity: 0.3,
    icon: '🔵',
    geom: 'polyline',
  },
};

// ── Fonds de carte ──────────────────────────────────────────────────────────

const FONDS_CARTE = {
  // The QC MSP orthophoto / carte_gouv_qc tile servers
  // (geoegl.msp.gouv.qc.ca/carto/tms/...) return 404 — the URLs have
  // changed since this code was written. Replaced with Esri World Imagery
  // (satellite, global coverage including QC) and OpenTopoMap (topographic).
  // The WFS service for foret_ouverte below still works and is unchanged.
  satellite: {
    fr: 'Satellite',
    en: 'Satellite',
    type: 'tile',
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: 'Tiles &copy; Esri — World Imagery',
    maxZoom: 19,
  },
  topo: {
    fr: 'Topographique',
    en: 'Topographic',
    type: 'tile',
    url: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
    attribution:
      'Map data: &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>, <a href="http://viewfinderpanoramas.org">SRTM</a> | Map style: &copy; <a href="https://opentopomap.org">OpenTopoMap</a>',
    maxZoom: 17,
  },
  foret_ouverte: {
    fr: 'Forêt ouverte QC',
    en: 'QC Open Forest',
    type: 'wms',
    url: 'https://geoegl.msp.gouv.qc.ca/ws/mffpecofor.fcgi',
    layers: 'peuplements_ecoforestiers',
    attribution: '&copy; MFFP — Forêt ouverte',
    maxZoom: 18,
  },
  osm: {
    fr: 'OpenStreetMap',
    en: 'OpenStreetMap',
    type: 'tile',
    url: 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    maxZoom: 19,
  },
};

// ── Outils gouvernementaux pour vérification de lot ─────────────────────────

const OUTILS_LOT = {
  QC: [
    { val: 'infolot', url: 'https://appli.foncier.gouv.qc.ca/infolot/', label: 'Infolot' },
    { val: 'foretouverte', url: 'https://www.foretouverte.gouv.qc.ca/', label: 'Forêt ouverte' },
  ],
  ON: [
    { val: 'geohub', url: 'https://geohub.lio.gov.on.ca/', label: 'Ontario GeoHub' },
    { val: 'mpac', url: 'https://www.mpac.ca/', label: 'MPAC' },
  ],
};

// ── Helpers GeoJSON ─────────────────────────────────────────────────────────

function centroidGeoJson(featureCollection) {
  if (!featureCollection?.features?.length) return null;
  const lats = [];
  const lngs = [];
  for (const f of featureCollection.features) {
    if (!f.geometry) continue;
    const g = f.geometry;
    if (g.type === 'Point') {
      lngs.push(g.coordinates[0]);
      lats.push(g.coordinates[1]);
    } else if (g.type === 'Polygon') {
      for (const [lng, lat] of g.coordinates[0] || []) {
        lngs.push(lng);
        lats.push(lat);
      }
    } else if (g.type === 'LineString') {
      for (const [lng, lat] of g.coordinates || []) {
        lngs.push(lng);
        lats.push(lat);
      }
    }
  }
  if (!lats.length) return null;
  return {
    lat: lats.reduce((a, b) => a + b, 0) / lats.length,
    lng: lngs.reduce((a, b) => a + b, 0) / lngs.length,
  };
}

function appliquerStyleZone(layer, typeZone) {
  const z = ZONES[typeZone];
  if (!z) return;
  if (typeof layer.setStyle === 'function') {
    layer.setStyle({
      color: z.couleur,
      fillColor: z.couleur,
      fillOpacity: z.fillOpacity,
      weight: 3,
    });
  }
  // Markers don't respond to setStyle — they're <img> icons. Replace the
  // default blue pin with a colored divIcon so the zone type's color is
  // visible on the map. We use a small filled circle with a white border so
  // it reads cleanly on satellite imagery.
  if (typeof layer.setIcon === 'function' && layer instanceof L.Marker) {
    const icon = L.divIcon({
      className: 'zone-marker',
      html: `<span style="
        display:block;
        width:18px;height:18px;
        border-radius:50%;
        background:${z.couleur};
        border:2px solid #fff;
        box-shadow:0 0 0 1px rgba(0,0,0,0.4);
      "></span>`,
      iconSize: [18, 18],
      iconAnchor: [9, 9],
    });
    layer.setIcon(icon);
  }
}

// Centres approximatifs des provinces
const CENTRES = {
  QC: { centre: [52.0, -72.0], zoom: 5 },
  ON: { centre: [50.0, -85.0], zoom: 5 },
};

// ── Traductions ───────────────────────────────────────────────────────────────

const T = {
  fr: {
    title: 'Enregistrer une terre',
    titleEdition: 'Modifier la terre',
    sousTitre: 'Mettez votre territoire à la disposition des chasseurs',
    sousTitreEdition: 'Mettre à jour les informations et les zones de votre terre.',
    chargementTerre: 'Chargement de la terre…',
    accesRefuse: 'Cette page est réservée aux propriétaires et agriculteurs.',
    retourTableau: 'Retour au tableau de bord',
    retourMesTerres: '← Mes terres',
    etape: (n, total) => `Étape ${n} sur ${total}`,
    steps: ['Informations de base', 'Localisation', 'Chasse', 'Photos & publication'],
    titreAnnonce: "Titre de l'annonce",
    titrePlaceholder: 'Ex. : Forêt privée de 50 ha au Saguenay',
    province: 'Province',
    qc: 'Québec',
    on: 'Ontario',
    municipality: 'Municipalité',
    mrc: 'MRC',
    municipalitePh: 'Ex. : Saint-Hyacinthe',
    mrcPh: 'Ex. : Les Maskoutains',
    lotQc: 'Numéro de lot (cadastre rénové QC, 7 chiffres)',
    pinOn: 'PIN Ontario (NNNNN-NNNN)',
    lotPh: 'Ex. : 4 523 891',
    pinPh: 'Ex. : 12345-6789',
    aideLotQc: 'Trouvez votre numéro sur votre acte notarié, compte de taxes ou sur Infolot.ca',
    aideLotOn: "Trouvez votre PIN sur votre title de propriété ou avis d'évaluation MPAC",
    verifierSur: 'Vérifier sur',
    confirmationLot:
      "J'ai vérifié mon lot sur l'outil gouvernemental et je confirme que les informations sont exactes. Je suis le propriétaire ou mandataire autorisé de ce terrain.",
    requireConfirmationLot:
      'Vous devez vérifier votre lot et cocher la confirmation avant de continuer.',
    superficie: 'Superficie (hectares)',
    superficiePh: 'Ex. : 42',
    milieu: 'Milieu dominant',
    choisir: 'Choisir…',
    fondCarte: 'Fond de carte',
    typeZone: 'Type de zone à dessiner',
    selectionnerType:
      'Sélectionnez un type, puis utilisez les outils en haut à droite de la carte pour dessiner.',
    legende: 'Légende des zones',
    effacerZones: 'Effacer toutes les zones',
    confirmerEffacer: 'Voulez-vous vraiment effacer toutes les zones dessinées ?',
    capturerCarte: 'Capturer la carte',
    captureEnCours: 'Capture en cours…',
    captureSucces: 'Capture sauvegardée. Cette carte sera intégrée dans votre contrat de location.',
    captureErreur:
      'Capture impossible (limites cross-origin du fond de carte). Réessayez avec un autre fond ou ignorez cette étape.',
    nbZones: (n) => `${n} zone${n > 1 ? 's' : ''} dessinée${n > 1 ? 's' : ''}`,
    aucuneZone: 'Aucune zone dessinée',
    requireZone:
      "Dessinez au moins une zone (ou un point d'accès / un sentier) avant de continuer.",
    especesDispos: 'Espèces disponibles',
    especesAideEn: 'Sélectionnez toutes les espèces présentes',
    capacite: 'Capacité maximale de chasseurs',
    capaciteAide: 'Nombre maximum de chasseurs simultanés sur le territoire',
    rules: 'Règles spéciales',
    reglesPh: 'Ex. : Pas de chasse au matin avant 7h. Stationnement à l’entrée nord uniquement.',
    services: 'Services offerts',
    photos: 'Photos du territoire',
    photosAide: "Ajoutez jusqu'à 5 photos (paysages, accès, services). Format JPG ou PNG.",
    ajouterPhoto: '+ Ajouter une photo',
    photoMax: 'Maximum 5 photos',
    supprimer: 'Supprimer',
    precedent: '← Précédent',
    suivant: 'Suivant →',
    brouillon: 'Enregistrer comme brouillon',
    publier: 'Publier et créer une annonce',
    enregistrement: 'Enregistrement…',
    erreur: 'Une erreur est survenue. Veuillez réessayer.',
    succesBrouillon: 'Terre enregistrée comme brouillon.',
    succesPublier: 'Terre publiée. Création de l’annonce…',
    requis: 'Champ requis',
    superficieInvalide: 'Superficie invalide',
    placerPoint: 'Veuillez placer le point GPS de la terre.',
    aucuneEspece: 'Sélectionnez au moins une espèce.',
  },
  en: {
    title: 'Register a property',
    titleEdition: 'Edit land',
    sousTitre: 'Make your land available to hunters',
    sousTitreEdition: 'Update your land information and zones.',
    chargementTerre: 'Loading land…',
    accesRefuse: 'This page is reserved for landowners and farmers.',
    retourTableau: 'Back to dashboard',
    retourMesTerres: '← My lands',
    etape: (n, total) => `Step ${n} of ${total}`,
    steps: ['Basic information', 'Location', 'Hunting', 'Photos & publication'],
    titreAnnonce: 'Listing title',
    titrePlaceholder: 'E.g. : Private 50 ha forest in Saguenay',
    province: 'Province',
    qc: 'Quebec',
    on: 'Ontario',
    municipality: 'Municipality',
    mrc: 'Regional county (MRC)',
    municipalitePh: 'E.g. : Saint-Hyacinthe',
    mrcPh: 'E.g. : Les Maskoutains',
    lotQc: 'Lot number (renewed QC cadastre, 7 digits)',
    pinOn: 'Ontario PIN (NNNNN-NNNN)',
    lotPh: 'E.g.: 4 523 891',
    pinPh: 'E.g.: 12345-6789',
    aideLotQc: 'Find your number on your notarial deed, tax bill or on Infolot.ca',
    aideLotOn: 'Find your PIN on your property title or MPAC assessment notice',
    verifierSur: 'Verify on',
    confirmationLot:
      'I verified my lot on the government tool and confirm the information is accurate. I am the owner or authorized representative of this property.',
    requireConfirmationLot:
      'You must verify your lot and check the confirmation before continuing.',
    superficie: 'Area (hectares)',
    superficiePh: 'E.g. : 42',
    milieu: 'Dominant habitat',
    choisir: 'Choose…',
    fondCarte: 'Base map',
    typeZone: 'Zone type to draw',
    selectionnerType: 'Select a type, then use the tools in the top right of the map to draw.',
    legende: 'Zone legend',
    effacerZones: 'Clear all zones',
    confirmerEffacer: 'Do you really want to clear all drawn zones?',
    capturerCarte: 'Capture map',
    captureEnCours: 'Capturing…',
    captureSucces: 'Capture saved. This map will be integrated into your rental contract.',
    captureErreur:
      'Capture not available (cross-origin limits on the base map). Try a different base map or skip this step.',
    nbZones: (n) => `${n} zone${n > 1 ? 's' : ''} drawn`,
    aucuneZone: 'No zones drawn',
    requireZone: 'Draw at least one zone (or access point / trail) before continuing.',
    especesDispos: 'Available species',
    especesAideEn: 'Select all species present',
    capacite: 'Maximum hunter capacity',
    capaciteAide: 'Maximum number of simultaneous hunters on the property',
    rules: 'Special rules',
    reglesPh: 'E.g. : No morning hunting before 7am. Parking at the north entrance only.',
    services: 'Available services',
    photos: 'Property photos',
    photosAide: 'Add up to 5 photos (landscapes, access, services). JPG or PNG format.',
    ajouterPhoto: '+ Add a photo',
    photoMax: 'Maximum 5 photos',
    supprimer: 'Remove',
    precedent: '← Previous',
    suivant: 'Next →',
    brouillon: 'Save as draft',
    publier: 'Publish and create listing',
    enregistrement: 'Saving…',
    erreur: 'An error occurred. Please try again.',
    succesBrouillon: 'Property saved as draft.',
    succesPublier: 'Property published. Creating listing…',
    requis: 'Required field',
    superficieInvalide: 'Invalid area',
    placerPoint: 'Please place the GPS point on the map.',
    aucuneEspece: 'Select at least one species.',
  },
};

function VueProvince({ province }) {
  const map = useMap();
  const ref = useRef(null);
  useEffect(() => {
    if (province && ref.current !== province) {
      ref.current = province;
      const c = CENTRES[province];
      if (c) map.setView(c.centre, c.zoom, { animate: true });
    }
  }, [province, map]);
  return null;
}

// ── Styles partagés ───────────────────────────────────────────────────────────

const inputStyle = {
  background: 'rgba(255,255,255,0.05)',
  border: `1px solid ${BORDURE}`,
  borderRadius: '6px',
  color: TEXTE,
  padding: '0.55rem 0.8rem',
  fontSize: '0.9rem',
  width: '100%',
  outline: 'none',
  boxSizing: 'border-box',
  fontFamily: 'inherit',
};

const labelStyle = {
  display: 'block',
  fontSize: '0.78rem',
  fontWeight: 600,
  color: TEXTE_DIM,
  marginBottom: '0.4rem',
  letterSpacing: '0.02em',
};

const erreurInline = {
  marginTop: '0.3rem',
  fontSize: '0.74rem',
  color: 'var(--couleur-texte-erreur)',
};

// ── Wizard ────────────────────────────────────────────────────────────────────

const NB_ETAPES = 4;

function BarreEtapes({ courante, t }) {
  return (
    <div style={{ display: 'flex', gap: '6px', marginBottom: '2rem' }}>
      {t.steps.map((label, i) => {
        const n = i + 1;
        const active = n === courante;
        const fait = n < courante;
        return (
          <div key={n} style={{ flex: 1, minWidth: 0 }}>
            <div
              style={{
                height: 4,
                borderRadius: 3,
                background: fait || active ? OR : 'rgba(255,255,255,0.1)',
                marginBottom: '0.5rem',
              }}
            />
            <div
              style={{
                fontSize: '0.72rem',
                color: active ? OR : fait ? TEXTE_DIM : 'rgba(255,255,255,0.3)',
                fontWeight: active ? 700 : 500,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {n}. {label}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Étapes ────────────────────────────────────────────────────────────────────

function Etape1({ form, setForm, erreurs, t, lang }) {
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <div style={{ display: 'grid', gap: '1.1rem' }}>
      <div>
        <label style={labelStyle}>{t.titreAnnonce} *</label>
        <input
          type="text"
          value={form.title}
          onChange={set('title')}
          placeholder={t.titrePlaceholder}
          style={inputStyle}
          maxLength={200}
        />
        {erreurs.title && <div style={erreurInline}>{erreurs.title}</div>}
      </div>

      <div>
        <label style={labelStyle}>{t.province} *</label>
        <div style={{ display: 'flex', gap: '0.6rem' }}>
          {[
            { val: 'QC', label: t.qc },
            { val: 'ON', label: t.on },
          ].map((p) => {
            const active = form.province === p.val;
            return (
              <button
                type="button"
                key={p.val}
                onClick={() => setForm((f) => ({ ...f, province: p.val }))}
                style={{
                  flex: 1,
                  padding: '0.55rem 1rem',
                  background: active ? `${OR}22` : 'rgba(255,255,255,0.04)',
                  border: `1px solid ${active ? OR : BORDURE}`,
                  color: active ? OR : TEXTE,
                  borderRadius: '6px',
                  fontSize: '0.9rem',
                  fontWeight: active ? 700 : 500,
                  cursor: 'pointer',
                  transition: 'all 0.15s',
                }}
              >
                {p.label}
              </button>
            );
          })}
        </div>
        {erreurs.province && <div style={erreurInline}>{erreurs.province}</div>}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        <div>
          <label style={labelStyle}>{t.municipality} *</label>
          <input
            type="text"
            value={form.municipality}
            onChange={set('municipality')}
            placeholder={t.municipalitePh}
            style={inputStyle}
          />
          {erreurs.municipality && <div style={erreurInline}>{erreurs.municipality}</div>}
        </div>
        <div>
          <label style={labelStyle}>{t.mrc}</label>
          <input
            type="text"
            value={form.mrc}
            onChange={set('mrc')}
            placeholder={t.mrcPh}
            style={inputStyle}
          />
        </div>
      </div>

      <div>
        <label style={labelStyle}>{form.province === 'ON' ? t.pinOn : t.lotQc}</label>
        <input
          type="text"
          value={form.lot}
          onChange={set('lot')}
          placeholder={form.province === 'ON' ? t.pinPh : t.lotPh}
          style={inputStyle}
        />
        <div
          style={{
            fontSize: '0.74rem',
            color: 'rgba(255,255,255,0.4)',
            marginTop: '0.4rem',
          }}
        >
          {form.province === 'ON' ? t.aideLotOn : t.aideLotQc}
        </div>

        {/* Boutons d'outils gouvernementaux */}
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', marginTop: '0.6rem' }}>
          {(OUTILS_LOT[form.province] || []).map((o) => (
            <a
              key={o.val}
              href={o.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontSize: '0.78rem',
                color: OR,
                background: `${OR}11`,
                border: `1px solid ${OR}55`,
                borderRadius: '5px',
                padding: '0.35rem 0.75rem',
                textDecoration: 'none',
                fontWeight: 600,
                whiteSpace: 'nowrap',
              }}
            >
              {t.verifierSur} {o.label} →
            </a>
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        <div>
          <label style={labelStyle}>{t.superficie} *</label>
          <input
            type="number"
            min={0.1}
            step={0.1}
            value={form.superficie}
            onChange={set('superficie')}
            placeholder={t.superficiePh}
            style={inputStyle}
          />
          {erreurs.superficie && <div style={erreurInline}>{erreurs.superficie}</div>}
        </div>
        <div>
          <label style={labelStyle}>{t.milieu} *</label>
          <select value={form.milieu} onChange={set('milieu')} style={inputStyle}>
            <option value="">{t.choisir}</option>
            {MILIEUX.map((m) => (
              <option key={m.val} value={m.val}>
                {m[lang]}
              </option>
            ))}
          </select>
          {erreurs.milieu && <div style={erreurInline}>{erreurs.milieu}</div>}
        </div>
      </div>

      {/* ── Confirmation obligatoire de vérification du lot ── */}
      <div
        style={{
          marginTop: '0.5rem',
          padding: '0.9rem 1.1rem',
          background: 'rgba(184,134,11,0.06)',
          border: `1px solid ${OR}55`,
          borderRadius: '6px',
        }}
      >
        <label
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '0.7rem',
            cursor: 'pointer',
            fontSize: '0.84rem',
            color: TEXTE,
            lineHeight: 1.45,
          }}
        >
          <input
            type="checkbox"
            checked={!!form.lot_verifie}
            onChange={(e) => setForm((f) => ({ ...f, lot_verifie: e.target.checked }))}
            style={{
              marginTop: 3,
              accentColor: OR,
              width: 18,
              height: 18,
              flex: '0 0 18px', // prevent the checkbox from stretching
              cursor: 'pointer',
            }}
          />
          <span style={{ flex: 1 }}>{t.confirmationLot}</span>
        </label>
        {erreurs.lot_verifie && (
          <div style={{ ...erreurInline, marginLeft: '1.6rem' }}>{erreurs.lot_verifie}</div>
        )}
      </div>
    </div>
  );
}

function Etape2({ form, setForm, erreurs, t, lang }) {
  const fondParDefaut = 'satellite';
  const fond = form.fondCarte || fondParDefaut;
  const fondConf = FONDS_CARTE[fond] || FONDS_CARTE.osm;

  const mapRef = useRef(null);
  const featureGroupRef = useRef(null);
  const [captureEtat, setCaptureEtat] = useState(''); // '', 'en_cours', 'succes', 'erreur'

  // Edit mode: when the form arrives with pre-existing zones_geojson, hydrate
  // the FeatureGroup with leaflet layers so the user can see + drag/edit them
  // via leaflet-draw. Hydration runs at most once per Etape2 mount.
  //
  // We race two triggers: (1) the FeatureGroup ref attaches *before* the
  // prefill arrives in form, and (2) the prefill arrives *before* the
  // FeatureGroup mounts. Either is possible depending on render order.
  // Whichever fires first calls `hydrater()`; the guard skips no-ops.
  const hydrateRef = useRef(false);
  const hydrater = useCallback(() => {
    if (hydrateRef.current) return;
    const fc = form.zones_geojson;
    const fg = featureGroupRef.current;
    if (!fc || !Array.isArray(fc.features) || fc.features.length === 0 || !fg) return;
    fc.features.forEach((feat) => {
      const layer = L.geoJSON(feat).getLayers()[0];
      if (!layer) return;
      layer.feature = feat;
      appliquerStyleZone(layer, feat.properties?.zone_type || 'chasse_autorisee');
      fg.addLayer(layer);
    });
    hydrateRef.current = true;
    // Fit the map to the loaded zones so the user lands on them.
    try {
      if (mapRef.current && fg.getBounds().isValid()) {
        mapRef.current.fitBounds(fg.getBounds(), { padding: [40, 40] });
      }
    } catch {
      /* getBounds throws on Point-only collections — ignore */
    }
  }, [form.zones_geojson]);

  // Trigger #1: state arrives after the FeatureGroup has mounted.
  useEffect(() => {
    hydrater();
  }, [hydrater]);

  // Trigger #2: FeatureGroup mounts after the state arrives. We attach via a
  // callback ref so we can run hydration synchronously the moment leaflet
  // creates the layer group.
  const featureGroupAttach = useCallback(
    (layer) => {
      featureGroupRef.current = layer;
      if (layer) hydrater();
    },
    [hydrater]
  );

  // Type de zone actuellement sélectionné — détermine la couleur appliquée
  // au prochain feature dessiné.
  const typeActif = form.type_zone_actif || 'chasse_autorisee';
  const setTypeActif = (val) => setForm((f) => ({ ...f, type_zone_actif: val }));

  // Conserve la liste de features dans le state, sous forme FeatureCollection.
  const features = form.zones_geojson?.features || [];

  const sauvegarderFeatures = (newFeatures) => {
    setForm((f) => ({
      ...f,
      zones_geojson: { type: 'FeatureCollection', features: newFeatures },
    }));
  };

  // ── Handlers leaflet-draw ───────────────────────────────────────────────

  const onCreated = (e) => {
    const layer = e.layer;
    const zone_type = typeActif;
    appliquerStyleZone(layer, zone_type);

    // Convertit la géométrie en GeoJSON et tague avec le type
    const geo = layer.toGeoJSON();
    geo.properties = { ...(geo.properties || {}), zone_type };
    layer.feature = geo;

    // Functional setter — drawing several polygons in a row was overwriting
    // each other because `features` (closure) was the value at the time the
    // handler was bound, not the latest state. Reading from `f` here makes
    // each new polygon append cleanly.
    setForm((f) => {
      const current = f.zones_geojson?.features || [];
      return {
        ...f,
        zones_geojson: { type: 'FeatureCollection', features: [...current, geo] },
      };
    });
  };

  const onEdited = (e) => {
    // Reconstruit la liste depuis tous les layers du FeatureGroup
    if (!featureGroupRef.current) return;
    const collected = [];
    featureGroupRef.current.eachLayer((l) => {
      if (typeof l.toGeoJSON !== 'function') return;
      const geo = l.toGeoJSON();
      geo.properties = {
        ...(l.feature?.properties || {}),
        ...(geo.properties || {}),
      };
      collected.push(geo);
    });
    sauvegarderFeatures(collected);
  };

  const onDeleted = () => {
    if (!featureGroupRef.current) {
      sauvegarderFeatures([]);
      return;
    }
    const remaining = [];
    featureGroupRef.current.eachLayer((l) => {
      if (typeof l.toGeoJSON !== 'function') return;
      const geo = l.toGeoJSON();
      geo.properties = {
        ...(l.feature?.properties || {}),
        ...(geo.properties || {}),
      };
      remaining.push(geo);
    });
    sauvegarderFeatures(remaining);
  };

  const effacerToutes = () => {
    if (!window.confirm(t.confirmerEffacer)) return;
    if (featureGroupRef.current) {
      featureGroupRef.current.clearLayers();
    }
    sauvegarderFeatures([]);
  };

  // ── Capture d'écran via leaflet-image ───────────────────────────────────
  // leaflet-image renders tile layers only — the leaflet-draw SVG overlay
  // (polygons / lines / markers) is NOT included. We re-draw the geojson
  // features on top of the returned canvas by projecting each lat/lng to
  // container pixels via the live map instance, so the saved PNG actually
  // shows the user's zones.

  const dessinerFeaturesSurCanvas = (canvas, map) => {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const fc = form.zones_geojson;
    if (!fc || !Array.isArray(fc.features) || fc.features.length === 0) return;

    // GeoJSON coordinates are [lng, lat]; leaflet wants L.latLng(lat, lng).
    const project = (coord) => map.latLngToContainerPoint([coord[1], coord[0]]);

    fc.features.forEach((feat) => {
      const zone = ZONES[feat.properties?.zone_type] || ZONES.chasse_autorisee;
      const geom = feat.geometry;
      if (!geom) return;

      ctx.lineWidth = 3;
      ctx.strokeStyle = zone.couleur;
      ctx.fillStyle =
        zone.couleur +
        Math.round((zone.fillOpacity ?? 0.3) * 255)
          .toString(16)
          .padStart(2, '0');

      if (geom.type === 'Polygon') {
        // Outer ring only (we don't draw holes — the wizard doesn't create any).
        const ring = geom.coordinates[0] || [];
        if (ring.length < 3) return;
        ctx.beginPath();
        ring.forEach((c, i) => {
          const p = project(c);
          if (i === 0) ctx.moveTo(p.x, p.y);
          else ctx.lineTo(p.x, p.y);
        });
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
      } else if (geom.type === 'LineString') {
        const coords = geom.coordinates || [];
        if (coords.length < 2) return;
        ctx.beginPath();
        ctx.lineWidth = 4;
        coords.forEach((c, i) => {
          const p = project(c);
          if (i === 0) ctx.moveTo(p.x, p.y);
          else ctx.lineTo(p.x, p.y);
        });
        ctx.stroke();
      } else if (geom.type === 'Point') {
        const p = project(geom.coordinates);
        const r = 7;
        ctx.beginPath();
        ctx.arc(p.x, p.y, r, 0, Math.PI * 2);
        ctx.fillStyle = zone.couleur;
        ctx.fill();
        ctx.lineWidth = 2;
        ctx.strokeStyle = '#fff';
        ctx.stroke();
      }
    });
  };

  const capturerCarte = () => {
    if (!mapRef.current) return;
    const map = mapRef.current;
    setCaptureEtat('en_cours');

    // leaflet-image crashes on divIcon markers because it tries to read
    // `marker.options.icon.options.iconUrl` and a divIcon has none. We
    // temporarily detach every marker layer, run the capture, then put them
    // back. The Point features in `zones_geojson` are re-drawn directly on
    // the canvas by `dessinerFeaturesSurCanvas` so nothing visual is lost.
    const fg = featureGroupRef.current;
    const detachedMarkers = [];
    if (fg) {
      fg.eachLayer((l) => {
        if (l instanceof L.Marker) detachedMarkers.push(l);
      });
      detachedMarkers.forEach((m) => fg.removeLayer(m));
    }
    const restoreMarkers = () => {
      if (!fg) return;
      detachedMarkers.forEach((m) => fg.addLayer(m));
    };

    leafletImage(map, (err, canvas) => {
      if (err || !canvas) {
        restoreMarkers();
        setCaptureEtat('erreur');
        return;
      }
      try {
        dessinerFeaturesSurCanvas(canvas, map);
        const dataUrl = canvas.toDataURL('image/png');
        setForm((f) => ({ ...f, snapshot_base64: dataUrl }));
        setCaptureEtat('succes');
      } catch (_) {
        setCaptureEtat('erreur');
      } finally {
        restoreMarkers();
      }
    });
  };

  return (
    <div style={{ display: 'grid', gap: '0.9rem' }}>
      {/* ── Sélecteur de fond de carte ── */}
      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        <span style={{ ...labelStyle, marginBottom: 0, alignSelf: 'center' }}>{t.fondCarte} :</span>
        {Object.entries(FONDS_CARTE).map(([val, conf]) => {
          const active = fond === val;
          return (
            <button
              type="button"
              key={val}
              onClick={() => setForm((f) => ({ ...f, fondCarte: val }))}
              style={{
                padding: '0.3rem 0.8rem',
                background: active ? `${OR}22` : 'transparent',
                border: `1px solid ${active ? OR : BORDURE}`,
                color: active ? OR : TEXTE_DIM,
                borderRadius: '5px',
                fontSize: '0.78rem',
                fontWeight: active ? 700 : 500,
                cursor: 'pointer',
              }}
            >
              {conf[lang]}
            </button>
          );
        })}
      </div>

      {/* ── Sélecteur de type de zone ── */}
      <div>
        <div style={{ ...labelStyle, marginBottom: '0.4rem' }}>{t.typeZone}</div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
            gap: '0.5rem',
          }}
        >
          {Object.entries(ZONES).map(([key, z]) => {
            const active = typeActif === key;
            return (
              <button
                type="button"
                key={key}
                onClick={() => setTypeActif(key)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.6rem',
                  padding: '0.55rem 0.8rem',
                  background: active ? `${z.couleur}33` : 'rgba(255,255,255,0.03)',
                  border: `1px solid ${active ? z.couleur : BORDURE}`,
                  color: active ? 'var(--couleur-texte)' : TEXTE_DIM,
                  borderRadius: '6px',
                  fontSize: '0.82rem',
                  fontWeight: active ? 700 : 500,
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <span style={{ fontSize: '1rem', flexShrink: 0 }}>{z.icon}</span>
                <span>{z[lang]}</span>
              </button>
            );
          })}
        </div>
        <div
          style={{
            fontSize: '0.74rem',
            color: 'rgba(255,255,255,0.4)',
            marginTop: '0.5rem',
          }}
        >
          {t.selectionnerType}
        </div>
      </div>

      {/* ── Carte avec drawing tools ── */}
      <div
        style={{
          height: '500px',
          borderRadius: '8px',
          overflow: 'hidden',
          border: `1px solid ${BORDURE}`,
        }}
      >
        {/* Page de dessin : on désactive le basemap par défaut de
            <ChasseHuntMap> (basemap=null) pour pouvoir switcher
            dynamiquement entre Esri / OpenTopoMap / WMS Forêt-ouverte / OSM.
            onMapReady capture l'instance Leaflet dans mapRef pour les
            opérations programmatiques (fitBounds après import GeoJSON,
            leaflet-image pour les screenshots, etc.). */}
        <ChasseHuntMap
          center={CENTRES[form.province]?.centre || [49.0, -75.0]}
          zoom={CENTRES[form.province]?.zoom || 5}
          basemap={null}
          height="100%"
          onMapReady={(map) => {
            mapRef.current = map;
          }}
        >
          {fondConf.type === 'wms' ? (
            <WMSTileLayer
              key={fond}
              url={fondConf.url}
              layers={fondConf.layers}
              format="image/png"
              transparent={true}
              attribution={fondConf.attribution}
            />
          ) : (
            <TileLayer
              key={fond}
              url={fondConf.url}
              attribution={fondConf.attribution}
              maxZoom={fondConf.maxZoom}
            />
          )}
          <VueProvince province={form.province} />

          <FeatureGroup ref={featureGroupAttach}>
            <EditControl
              // Remount the draw control when the active zone type changes —
              // shapeOptions are read once on construction, so without this
              // every polygon would be drawn in the initial color (green)
              // regardless of which zone button the user picked.
              key={typeActif}
              position="topright"
              onCreated={onCreated}
              onEdited={onEdited}
              onDeleted={onDeleted}
              draw={{
                rectangle: false,
                circle: false,
                circlemarker: false,
                polygon: {
                  shapeOptions: {
                    color: ZONES[typeActif]?.couleur || OR,
                    fillColor: ZONES[typeActif]?.couleur || OR,
                    fillOpacity: ZONES[typeActif]?.fillOpacity || 0.3,
                    weight: 3,
                  },
                },
                polyline: {
                  shapeOptions: {
                    color: ZONES[typeActif]?.couleur || OR,
                    weight: 4,
                  },
                },
                marker: true,
              }}
            />
          </FeatureGroup>
        </ChasseHuntMap>
      </div>

      {/* ── Légende + actions ── */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '0.8rem',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem' }}>
          {Object.entries(ZONES).map(([key, z]) => (
            <div
              key={key}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.4rem',
                fontSize: '0.74rem',
                color: TEXTE_DIM,
              }}
            >
              <span
                style={{
                  display: 'inline-block',
                  width: 14,
                  height: 14,
                  borderRadius: z.geom === 'marker' ? '50%' : '3px',
                  background: z.geom === 'polygon' ? `${z.couleur}55` : z.couleur,
                  border: `2px solid ${z.couleur}`,
                }}
              />
              {z[lang]}
            </div>
          ))}
        </div>

        <button
          type="button"
          onClick={effacerToutes}
          disabled={features.length === 0}
          style={{
            background: 'transparent',
            border: `1px solid ${features.length === 0 ? BORDURE : '#e53e3e88'}`,
            color: features.length === 0 ? 'rgba(255,255,255,0.3)' : '#e53e3e',
            borderRadius: '5px',
            padding: '0.4rem 0.85rem',
            fontSize: '0.78rem',
            fontWeight: 600,
            cursor: features.length === 0 ? 'not-allowed' : 'pointer',
          }}
        >
          ✕ {t.effacerZones}
        </button>
      </div>

      {/* ── Compteur + bouton capture ── */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '0.8rem',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'rgba(255,255,255,0.03)',
          border: `1px solid ${BORDURE}`,
          borderRadius: '6px',
          padding: '0.7rem 0.9rem',
        }}
      >
        <span
          style={{ fontSize: '0.82rem', color: features.length ? OR : TEXTE_DIM, fontWeight: 600 }}
        >
          {features.length ? `📐 ${t.nbZones(features.length)}` : t.aucuneZone}
        </span>

        <button
          type="button"
          onClick={capturerCarte}
          disabled={captureEtat === 'en_cours'}
          style={{
            background: `${OR}22`,
            border: `1px solid ${OR}`,
            color: OR,
            borderRadius: '5px',
            padding: '0.45rem 1rem',
            fontSize: '0.82rem',
            fontWeight: 700,
            cursor: captureEtat === 'en_cours' ? 'not-allowed' : 'pointer',
            opacity: captureEtat === 'en_cours' ? 0.6 : 1,
          }}
        >
          📸 {captureEtat === 'en_cours' ? t.captureEnCours : t.capturerCarte}
        </button>
      </div>

      {/* ── Résultat capture ── */}
      {captureEtat === 'succes' && form.snapshot_base64 && (
        <div
          style={{
            background: 'rgba(40,160,80,0.08)',
            border: '1px solid rgba(40,160,80,0.4)',
            borderRadius: '6px',
            padding: '0.8rem',
            display: 'grid',
            gap: '0.6rem',
          }}
        >
          <div style={{ fontSize: '0.82rem', color: 'var(--couleur-texte-succes)' }}>
            ✓ {t.captureSucces}
          </div>
          <img
            src={form.snapshot_base64}
            alt="capture carte"
            style={{
              maxWidth: '100%',
              maxHeight: 200,
              borderRadius: '4px',
              border: `1px solid ${BORDURE}`,
            }}
          />
        </div>
      )}
      {captureEtat === 'erreur' && (
        <div
          style={{
            background: 'rgba(220,160,40,0.1)',
            border: '1px solid rgba(220,160,40,0.45)',
            borderRadius: '6px',
            padding: '0.7rem 0.9rem',
            fontSize: '0.82rem',
            color: 'var(--couleur-texte-warning)',
          }}
        >
          ⚠ {t.captureErreur}
        </div>
      )}

      {erreurs.zones && <div style={erreurInline}>{erreurs.zones}</div>}
    </div>
  );
}

function Etape3({ form, setForm, erreurs, t, lang }) {
  const toggleEspece = (val) => {
    setForm((f) => {
      const set = new Set(f.especes);
      if (set.has(val)) set.delete(val);
      else set.add(val);
      return { ...f, especes: Array.from(set) };
    });
  };

  const toggleService = (val) => {
    setForm((f) => {
      const set = new Set(f.services);
      if (set.has(val)) set.delete(val);
      else set.add(val);
      return { ...f, services: Array.from(set) };
    });
  };

  return (
    <div style={{ display: 'grid', gap: '1.4rem' }}>
      <div>
        <label style={labelStyle}>{t.especesDispos} *</label>
        <div
          style={{ fontSize: '0.74rem', color: 'rgba(255,255,255,0.35)', marginBottom: '0.6rem' }}
        >
          {t.especesAideEn}
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))',
            gap: '0.5rem',
          }}
        >
          {ESPECES.map((esp) => {
            const active = form.especes.includes(esp.val);
            return (
              <label
                key={esp.val}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  gap: '0.6rem',
                  padding: '0.55rem 0.8rem',
                  background: active ? `${VERT}55` : 'rgba(255,255,255,0.03)',
                  border: `1px solid ${active ? OR : BORDURE}`,
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  color: active ? 'var(--couleur-texte)' : TEXTE_DIM,
                  fontWeight: active ? 600 : 500,
                  transition: 'all 0.12s',
                  minHeight: '2.4rem',
                  boxSizing: 'border-box',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                <input
                  type="checkbox"
                  checked={active}
                  onChange={() => toggleEspece(esp.val)}
                  style={{
                    accentColor: OR,
                    width: 16,
                    height: 16,
                    flex: '0 0 16px', // critical: don't let the checkbox stretch
                    margin: 0,
                    cursor: 'pointer',
                  }}
                />
                <span
                  style={{
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {esp[lang]}
                </span>
              </label>
            );
          })}
        </div>
        {erreurs.especes && <div style={erreurInline}>{erreurs.especes}</div>}
      </div>

      <div>
        <label style={labelStyle}>{t.capacite} *</label>
        <div
          style={{ fontSize: '0.74rem', color: 'rgba(255,255,255,0.35)', marginBottom: '0.6rem' }}
        >
          {t.capaciteAide}
        </div>
        <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap' }}>
          {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => {
            const active = form.capacite === n;
            return (
              <button
                type="button"
                key={n}
                onClick={() => setForm((f) => ({ ...f, capacite: n }))}
                style={{
                  width: 44,
                  height: 44,
                  background: active ? OR : 'rgba(255,255,255,0.05)',
                  border: `1px solid ${active ? OR : BORDURE}`,
                  color: active ? 'var(--couleur-fond-carte)' : TEXTE,
                  borderRadius: '6px',
                  fontSize: '0.95rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  transition: 'all 0.12s',
                }}
              >
                {n}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <label style={labelStyle}>{t.rules}</label>
        <textarea
          value={form.rules}
          onChange={(e) => setForm((f) => ({ ...f, rules: e.target.value }))}
          placeholder={t.reglesPh}
          rows={4}
          style={{ ...inputStyle, resize: 'vertical', minHeight: '90px' }}
        />
      </div>

      <div>
        <label style={labelStyle}>{t.services}</label>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))',
            gap: '0.5rem',
          }}
        >
          {SERVICES.map((s) => {
            const active = form.services.includes(s.val);
            return (
              <label
                key={s.val}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  gap: '0.6rem',
                  padding: '0.55rem 0.8rem',
                  background: active ? `${VERT}55` : 'rgba(255,255,255,0.03)',
                  border: `1px solid ${active ? OR : BORDURE}`,
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  color: active ? 'var(--couleur-texte)' : TEXTE_DIM,
                  fontWeight: active ? 600 : 500,
                  transition: 'all 0.12s',
                  minHeight: '2.4rem',
                  boxSizing: 'border-box',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                <input
                  type="checkbox"
                  checked={active}
                  onChange={() => toggleService(s.val)}
                  style={{
                    accentColor: OR,
                    width: 16,
                    height: 16,
                    flex: '0 0 16px',
                    margin: 0,
                    cursor: 'pointer',
                  }}
                />
                <span
                  style={{
                    flex: 1,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {s[lang]}
                </span>
              </label>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function Etape4({ form, setForm, t }) {
  const fileInputRef = useRef(null);

  const onFiles = (e) => {
    const files = Array.from(e.target.files || []);
    const restantes = 5 - form.photos.length;
    const aLire = files.slice(0, restantes);
    aLire.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setForm((f) => ({
          ...f,
          photos:
            f.photos.length >= 5
              ? f.photos
              : [
                  ...f.photos,
                  {
                    name: file.name,
                    taille: file.size,
                    data: ev.target.result,
                  },
                ],
        }));
      };
      reader.readAsDataURL(file);
    });
    e.target.value = '';
  };

  const supprimer = (idx) => {
    setForm((f) => ({ ...f, photos: f.photos.filter((_, i) => i !== idx) }));
  };

  return (
    <div style={{ display: 'grid', gap: '1.2rem' }}>
      <div>
        <label style={labelStyle}>{t.photos}</label>
        <div
          style={{ fontSize: '0.74rem', color: 'rgba(255,255,255,0.4)', marginBottom: '0.7rem' }}
        >
          {t.photosAide}
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
            gap: '0.7rem',
          }}
        >
          {form.photos.map((p, i) => (
            <div
              key={i}
              style={{
                position: 'relative',
                aspectRatio: '4 / 3',
                borderRadius: '6px',
                overflow: 'hidden',
                border: `1px solid ${BORDURE}`,
                background: '#000',
              }}
            >
              <img
                src={p.data}
                alt={p.name}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
              <button
                type="button"
                onClick={() => supprimer(i)}
                aria-label={t.supprimer}
                style={{
                  position: 'absolute',
                  top: 6,
                  right: 6,
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  background: 'rgba(0,0,0,0.75)',
                  border: '1px solid rgba(255,255,255,0.4)',
                  color: '#fff',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                  lineHeight: 1,
                  padding: 0,
                }}
              >
                ×
              </button>
            </div>
          ))}

          {form.photos.length < 5 && (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              style={{
                aspectRatio: '4 / 3',
                background: 'rgba(255,255,255,0.03)',
                border: `1px dashed ${OR}88`,
                borderRadius: '6px',
                color: OR,
                fontSize: '0.82rem',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {t.ajouterPhoto}
            </button>
          )}
        </div>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png"
          multiple
          onChange={onFiles}
          style={{ display: 'none' }}
        />

        <div style={{ marginTop: '0.5rem', fontSize: '0.72rem', color: 'rgba(255,255,255,0.3)' }}>
          {form.photos.length} / 5 — {t.photoMax}
        </div>
      </div>
    </div>
  );
}

// ── Validation ────────────────────────────────────────────────────────────────

function validerEtape(n, form, t) {
  const erreurs = {};
  if (n === 1) {
    if (!form.title.trim()) erreurs.title = t.requis;
    if (!form.province) erreurs.province = t.requis;
    if (!form.municipality.trim()) erreurs.municipality = t.requis;
    const sup = parseFloat(form.superficie);
    if (!form.superficie || isNaN(sup) || sup <= 0) erreurs.superficie = t.superficieInvalide;
    if (!form.milieu) erreurs.milieu = t.requis;
    if (!form.lot_verifie) erreurs.lot_verifie = t.requireConfirmationLot;
  }
  if (n === 2) {
    const nbFeatures = form.zones_geojson?.features?.length || 0;
    if (nbFeatures === 0) erreurs.zones = t.requireZone;
  }
  if (n === 3) {
    if (form.especes.length === 0) erreurs.especes = t.aucuneEspece;
  }
  return erreurs;
}

// ── Composant principal ───────────────────────────────────────────────────────

const FORM_INIT = {
  // Étape 1
  title: '',
  province: 'QC',
  municipality: '',
  mrc: '',
  lot: '',
  superficie: '',
  milieu: '',
  lot_verifie: false,
  // Étape 2
  fondCarte: '',
  type_zone_actif: 'chasse_autorisee',
  zones_geojson: null, // FeatureCollection des zones dessinées
  snapshot_base64: null, // Capture PNG de la carte
  // Étape 3
  especes: [],
  capacite: 2,
  rules: '',
  services: [],
  // Étape 4
  photos: [],
};

// Reverse-engineer the wizard fields baked into the description text on
// create. The create flow concatenates lines like:
//   "Milieu : Forêt mixte"
//   "Lot : 1 234 567"  (QC)  /  "PIN : ..."  (ON)
//   "Services : Cabane, Mirador"
// We parse what we can on edit so the user sees the right pre-selected
// chips/radio. Anything that doesn't match a known prefix is treated as a
// free-text description left as-is.
function hydraterDescription(description) {
  const out = { milieu: '', lot: '', services: [], rules: '' };
  if (!description) return out;
  const restantes = [];
  description.split('\n').forEach((line) => {
    const m = line.match(/^(Milieu|Lot|PIN|Services)\s*:\s*(.+)$/i);
    if (!m) {
      if (line.trim()) restantes.push(line);
      return;
    }
    const [, key, val] = m;
    if (/^Milieu$/i.test(key)) {
      const found = MILIEUX.find(
        (o) => o.fr === val.trim() || o.en === val.trim() || o.val === val.trim().toLowerCase()
      );
      if (found) out.milieu = found.val;
    } else if (/^Lot|PIN$/i.test(key)) {
      out.lot = val.trim();
    } else if (/^Services$/i.test(key)) {
      const labels = val.split(',').map((s) => s.trim());
      out.services = labels
        .map((l) => SERVICES.find((s) => s.fr === l || s.en === l || s.val === l.toLowerCase()))
        .filter(Boolean)
        .map((s) => s.val);
    }
  });
  out.rules = restantes.join('\n').trim();
  return out;
}

export default function EnregistrerTerre() {
  const { lang } = useContext(LangContext);
  const { utilisateur, estConnecte } = useAuth();
  const navigate = useNavigate();
  const { id: idEdition } = useParams(); // present iff route is /lands/:id/edit
  const modeEdition = Boolean(idEdition);
  const t = T[lang];

  const [etape, setEtape] = useState(1);
  const [form, setForm] = useState(FORM_INIT);
  const [erreurs, setErreurs] = useState({});
  const [envoi, setEnvoi] = useState(false);
  const [erreurApi, setErreurApi] = useState('');
  const [succes, setSucces] = useState('');
  const [chargementInitial, setChargementInitial] = useState(modeEdition);

  // Edit mode: fetch the land once on mount and prefill the wizard form.
  // The user moves through the same 4 steps; on save we PUT instead of POST.
  useEffect(() => {
    if (!modeEdition) return;
    let annule = false;
    api
      .get(`/lands/${idEdition}`)
      .then((res) => {
        if (annule) return;
        const land = res.data || {};
        const parsed = hydraterDescription(land.description || '');
        // Address concat (municipality, mrc, province) is irreversible; we
        // stash the whole string into `municipality` so it remains visible
        // and editable as a single field. The user can re-split if needed.
        setForm({
          ...FORM_INIT,
          title: land.title || '',
          province: land.province || 'QC',
          municipality: land.address || '',
          mrc: '',
          lot: parsed.lot,
          superficie: land.area_hectares != null ? String(land.area_hectares) : '',
          milieu: parsed.milieu,
          lot_verifie: true, // already-published land — verified by definition
          fondCarte: '',
          type_zone_actif: 'chasse_autorisee',
          zones_geojson: land.hunting_zones_geojson || null,
          snapshot_base64: land.map_snapshot_base64 || null,
          especes: Array.isArray(land.available_species) ? land.available_species : [],
          capacite: land.max_hunters || 2,
          rules: land.rules || parsed.rules || '',
          services: parsed.services,
          // Photos arrive as URLs; the wizard's photo card just needs a `data`
          // field for <img src> — URL or data: URL both work.
          photos: (Array.isArray(land.photos) ? land.photos : [])
            .filter(Boolean)
            .map((url, i) => ({ data: url, name: `photo-${i + 1}` })),
        });
      })
      .catch((err) => {
        if (!annule) setErreurApi(err.response?.data?.error || t.erreur);
      })
      .finally(() => {
        if (!annule) setChargementInitial(false);
      });
    return () => {
      annule = true;
    };
  }, [idEdition]);

  // ── Garde rôle ──────────────────────────────────────────────────────────
  const roleAutorise = useMemo(() => {
    const r = utilisateur?.user_type;
    return r === 'landowner' || r === 'farmer';
  }, [utilisateur]);

  if (!estConnecte) {
    return <Navigate to="/login" replace />;
  }

  if (utilisateur && !roleAutorise) {
    return (
      <div style={{ minHeight: '100vh', background: FOND, color: TEXTE }}>
        <Header />
        <div style={{ maxWidth: 600, margin: '4rem auto', padding: '2rem', textAlign: 'center' }}>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🚫</div>
          <h1
            style={{
              fontFamily: 'Georgia, serif',
              color: 'var(--couleur-texte)',
              marginBottom: '0.6rem',
            }}
          >
            {t.accesRefuse}
          </h1>
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              marginTop: '1.5rem',
              background: OR,
              color: 'var(--couleur-fond-carte)',
              border: 'none',
              padding: '0.6rem 1.4rem',
              borderRadius: '6px',
              fontSize: '0.9rem',
              fontWeight: 700,
              cursor: 'pointer',
            }}
          >
            ← {t.retourTableau}
          </button>
        </div>
      </div>
    );
  }

  // ── Navigation entre étapes ────────────────────────────────────────────

  const suivant = () => {
    const e = validerEtape(etape, form, t);
    setErreurs(e);
    if (Object.keys(e).length === 0) {
      setEtape((n) => Math.min(NB_ETAPES, n + 1));
    }
  };

  const precedent = () => {
    setErreurs({});
    setEtape((n) => Math.max(1, n - 1));
  };

  // ── Soumission ─────────────────────────────────────────────────────────

  const construirePayload = () => {
    const address = [form.municipality, form.mrc, form.province].filter(Boolean).join(', ');

    const description = [
      form.milieu &&
        `Milieu : ${MILIEUX.find((m) => m.val === form.milieu)?.[lang] || form.milieu}`,
      form.lot && (form.province === 'ON' ? `PIN : ${form.lot}` : `Lot : ${form.lot}`),
      form.services.length > 0 &&
        `Services : ${form.services.map((s) => SERVICES.find((x) => x.val === s)?.[lang] || s).join(', ')}`,
    ]
      .filter(Boolean)
      .join('\n');

    // Centroid des zones dessinées comme point GPS principal de la terre
    const centroid = centroidGeoJson(form.zones_geojson);

    return {
      title: form.title.trim(),
      description: description || null,
      province: form.province,
      area_hectares: parseFloat(form.superficie),
      address: address || null,
      latitude: centroid?.lat ?? null,
      longitude: centroid?.lng ?? null,
      photos: form.photos.map((p) => p.data),
      available_species: form.especes,
      rules: form.rules || null,
      max_hunters: form.capacite,
      hunting_zones_geojson: form.zones_geojson || null,
      map_snapshot_base64: form.snapshot_base64 || null,
    };
  };

  const enregistrer = async (avecEncan) => {
    // Valider toutes les étapes obligatoires
    for (let n = 1; n <= 3; n++) {
      const e = validerEtape(n, form, t);
      if (Object.keys(e).length > 0) {
        setErreurs(e);
        setEtape(n);
        return;
      }
    }

    setEnvoi(true);
    setErreurApi('');
    setSucces('');

    try {
      // Edit mode (URL = /lands/:id/edit) → PUT existing land. Create mode
      // (URL = /register-land) → POST new land. The payload shape is
      // identical between the two — only the verb changes.
      const res = modeEdition
        ? await api.put(`/lands/${idEdition}`, construirePayload())
        : await api.post('/lands', construirePayload());
      setSucces(avecEncan ? t.succesPublier : t.succesBrouillon);
      const landId = res.data?.id || idEdition;
      setTimeout(() => {
        if (modeEdition) {
          // Return to the land detail page so the user sees the changes live.
          navigate(`/lands/${landId}`, { state: { from: '/my-lands' } });
        } else if (avecEncan && landId) {
          navigate(`/create-auction?land=${landId}`);
        } else {
          navigate('/my-lands');
        }
      }, 800);
    } catch (err) {
      setErreurApi(err.response?.data?.error || t.erreur);
    } finally {
      setEnvoi(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <PrivateLayout>
      <div style={{ maxWidth: 820, margin: '0 auto', padding: '2rem 1.5rem 5rem' }}>
        {/* Back link to /my-lands. Kept above the form header so the user can
            bail out of the wizard at any step without losing context — the
            sidebar already exposes the rest of the app, but a one-tap return
            to the lands list matches the request flow. */}
        <Link
          to="/my-lands"
          style={{
            display: 'inline-block',
            fontSize: '0.85rem',
            color: 'rgba(255,255,255,0.5)',
            textDecoration: 'none',
            marginBottom: '1rem',
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.85)')}
          onMouseLeave={(e) => (e.currentTarget.style.color = 'rgba(255,255,255,0.5)')}
        >
          {t.retourMesTerres}
        </Link>

        {/* Loading guard for edit mode — the wizard needs the existing land's
            fields hydrated before rendering any step. Without this the user
            would briefly see a blank wizard, then content snap in. */}
        {chargementInitial && (
          <div
            style={{
              background: 'rgba(255,255,255,0.02)',
              border: `1px solid ${BORDURE}`,
              borderRadius: '8px',
              padding: '1.4rem',
              textAlign: 'center',
              color: TEXTE_DIM,
              fontSize: '0.9rem',
            }}
          >
            {t.chargementTerre}
          </div>
        )}

        {/* En-tête */}
        {!chargementInitial && (
          <>
            <div
              style={{
                background: `${VERT}55`,
                border: `1px solid ${OR}55`,
                borderRadius: '10px',
                padding: '1.4rem 1.6rem',
                marginBottom: '2rem',
              }}
            >
              <div
                style={{
                  fontSize: '0.72rem',
                  color: OR,
                  fontWeight: 700,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  marginBottom: '0.35rem',
                }}
              >
                {t.etape(etape, NB_ETAPES)}
              </div>
              <h1
                style={{
                  fontFamily: 'Georgia, serif',
                  fontSize: '1.55rem',
                  color: 'var(--couleur-texte)',
                  margin: '0 0 0.25rem',
                }}
              >
                {modeEdition ? `✏️ ${t.titleEdition}` : `🏡 ${t.title}`}
              </h1>
              <div style={{ fontSize: '0.85rem', color: TEXTE_DIM }}>
                {modeEdition ? t.sousTitreEdition : t.sousTitre}
              </div>
            </div>

            {/* Barre étapes */}
            <BarreEtapes courante={etape} t={t} />

            {/* Étape courante */}
            <div
              style={{
                background: 'rgba(255,255,255,0.02)',
                border: `1px solid ${BORDURE}`,
                borderRadius: '10px',
                padding: '1.6rem',
                marginBottom: '1.5rem',
              }}
            >
              {etape === 1 && (
                <Etape1 form={form} setForm={setForm} erreurs={erreurs} t={t} lang={lang} />
              )}
              {etape === 2 && (
                <Etape2 form={form} setForm={setForm} erreurs={erreurs} t={t} lang={lang} />
              )}
              {etape === 3 && (
                <Etape3 form={form} setForm={setForm} erreurs={erreurs} t={t} lang={lang} />
              )}
              {etape === 4 && <Etape4 form={form} setForm={setForm} t={t} />}
            </div>

            {/* Messages globaux */}
            {erreurApi && (
              <div
                style={{
                  background: 'rgba(200,50,50,0.12)',
                  border: '1px solid rgba(200,50,50,0.4)',
                  borderRadius: '6px',
                  padding: '0.7rem 1rem',
                  marginBottom: '1rem',
                  color: 'var(--couleur-texte-erreur)',
                  fontSize: '0.85rem',
                }}
              >
                ⚠ {erreurApi}
              </div>
            )}
            {succes && (
              <div
                style={{
                  background: 'rgba(40,160,80,0.12)',
                  border: '1px solid rgba(40,160,80,0.4)',
                  borderRadius: '6px',
                  padding: '0.7rem 1rem',
                  marginBottom: '1rem',
                  color: 'var(--couleur-texte-succes)',
                  fontSize: '0.85rem',
                }}
              >
                ✓ {succes}
              </div>
            )}

            {/* Boutons de navigation */}
            <div
              style={{
                display: 'flex',
                gap: '0.7rem',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
              }}
            >
              <button
                type="button"
                onClick={precedent}
                disabled={etape === 1 || envoi}
                style={{
                  background: 'transparent',
                  border: `1px solid ${BORDURE}`,
                  color: etape === 1 ? 'rgba(255,255,255,0.2)' : TEXTE,
                  padding: '0.6rem 1.3rem',
                  borderRadius: '6px',
                  fontSize: '0.88rem',
                  fontWeight: 500,
                  cursor: etape === 1 || envoi ? 'not-allowed' : 'pointer',
                  opacity: etape === 1 || envoi ? 0.5 : 1,
                }}
              >
                {t.precedent}
              </button>

              {etape < NB_ETAPES && (
                <button
                  type="button"
                  onClick={suivant}
                  style={{
                    background: OR,
                    border: `1px solid ${OR}`,
                    color: 'var(--couleur-fond-carte)',
                    padding: '0.6rem 1.6rem',
                    borderRadius: '6px',
                    fontSize: '0.9rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  {t.suivant}
                </button>
              )}

              {etape === NB_ETAPES && (
                <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => enregistrer(false)}
                    disabled={envoi}
                    style={{
                      background: 'transparent',
                      border: `1px solid ${OR}`,
                      color: OR,
                      padding: '0.6rem 1.3rem',
                      borderRadius: '6px',
                      fontSize: '0.88rem',
                      fontWeight: 600,
                      cursor: envoi ? 'not-allowed' : 'pointer',
                      opacity: envoi ? 0.6 : 1,
                    }}
                  >
                    {envoi ? t.enregistrement : t.brouillon}
                  </button>
                  <button
                    type="button"
                    onClick={() => enregistrer(true)}
                    disabled={envoi}
                    style={{
                      background: OR,
                      border: `1px solid ${OR}`,
                      color: 'var(--couleur-fond-carte)',
                      padding: '0.6rem 1.6rem',
                      borderRadius: '6px',
                      fontSize: '0.9rem',
                      fontWeight: 700,
                      cursor: envoi ? 'not-allowed' : 'pointer',
                      opacity: envoi ? 0.6 : 1,
                    }}
                  >
                    {envoi ? t.enregistrement : t.publier}
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </PrivateLayout>
  );
}
